import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Moment from "moment";
import { Button, Dialog, DialogActions, DialogContent } from '@material-ui/core';
import { IPersonProfileRelationData } from './PersonProfileDomainModel';
import { DatesFieldFormRow } from '../../../common/controls/Relations/FormsControls';
import { IConfig } from './Models';
import DialogTitleWithClose from '../../../common/components/DialogTitleWithClose';
import lang from '../../../common/controls/Relations/RelationsLang';

interface IEditRowControlResult {
	from: Moment.Moment;
	to: Moment.Moment;
};

interface IProps {
	editingRow: IPersonProfileRelationData
	onCancel: () => void,
	onSaved: (data: IEditRowControlResult) => void,
	config: IConfig,
}

export default function EditingRowDialog(props: IProps) {
	const { t } = useTranslation();

	const [selectedFrom, setSelectedFrom] = React.useState<Moment.Moment>(null);
	const [selectedTo, setSelectedTo] = React.useState<Moment.Moment>(null);
	const [dateError, setDateError] = React.useState("");

	React.useEffect(() => {
		let from = null;
		let to = null;
		if (props.editingRow?.from) {
			from = Moment(props.editingRow.from)
		}
		if (props.editingRow?.to) {
			to = Moment(props.editingRow.to)
		}

		setDateError("");
		setSelectedFrom(from);
		setSelectedTo(to);

	}, [props.editingRow])

	function handleSaveRow() {
		if (isValid()) {
			props.onSaved(getValues());
		}
	}

	function isValid(): boolean {
		let result = true;
		let dateError = "";

		const fieldsConfig = props.config.fields;

		if (fieldsConfig.dateFrom.required && !selectedFrom) {
			const label: string = lang.getDefaultByPrefix(props.config.fields.dateFrom, "label", "from");
			dateError = t("control.relation.requireErrorMessage", { label });
			result = false;
		}

		if (fieldsConfig.dateTo.required && !selectedTo) {
			const label: string = lang.getDefaultByPrefix(props.config.fields.dateTo, "label", "to");
			dateError = t("control.relation.requireErrorMessage", { label });
			result = false;
		}

		if (selectedFrom && !selectedFrom.isValid()) {
			result = false;
		}
		if (selectedTo && !selectedTo.isValid()) {
			result = false;
		}

		if (!result) {
			setDateError(dateError);
			return result;
		}

		let from = selectedFrom;
		if (!from) {
			from = Moment();
		}

		let to = selectedTo;

		if (to) {
			if (Moment(from) > Moment(to)) {
				dateError = "control.relation.fromToErrorMessage";
				result = false;
			}
		}
		setDateError(dateError);
		return result;
	}

	function getValues(): IEditRowControlResult {
		let from = selectedFrom;
		if (!from) {
			from = Moment();
		}

		return {
			from: from,
			to: selectedTo,
		}
	}

	if (!props.editingRow)
		return null;

	return <Dialog
		open={true}
		onClose={props.onCancel}
		disableBackdropClick
	>
		<DialogTitleWithClose onClose={props.onCancel}>
			{t("control.relation.editing")} {props.editingRow.resource.name}
		</DialogTitleWithClose>
		<DialogContent>
			<div className="edit-input-row">

				<DatesFieldFormRow
					from={selectedFrom}
					to={selectedTo}
					onChange={(from, to) => {
						setSelectedFrom(from);
						setSelectedTo(to);
					}}
					errorText={dateError}
					dateFromConfig={props.config.fields.dateFrom}
					dateToConfig={props.config.fields.dateTo}
					editRow={!!props.editingRow.id}
				/>

			</div>
		</DialogContent>
		<DialogActions>
			<Button onClick={props.onCancel}>
				{t("common.action.cancel")}
			</Button>
			<Button onClick={handleSaveRow} color="primary" variant="contained" >
				{t("common.action.save")}
			</Button>
		</DialogActions>
	</Dialog>
}